import * as React from 'react';
import { cn } from '@/lib/utils';
import { premiumTheme } from './tokens';

/**
 * AppCard \u2014 Premium SaaS base card primitive.
 *
 * Styling:
 * - Token-driven surface (`bg-surface-premium` + `border-border-hairline`)
 * - Rounded 14px by default
 * - No heavy shadow (hairline border + optional hover promotion)
 *
 * Replaces ad-hoc card markup. Uses CSS tokens so dark mode is automatic.
 */

export interface AppCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Remove internal padding */
  noPadding?: boolean;
  /** Remove border (e.g. when nested inside another card) */
  noBorder?: boolean;
  /** Promote border on hover (for list/clickable cards) */
  interactive?: boolean;
  /** Padding size token */
  padding?: 'xs' | 'sm' | 'md' | 'lg';
  /** Surface variant */
  variant?: 'card' | 'muted' | 'sunken';
  /** Accessible label when the card is interactive */
  asRole?: 'group' | 'article' | 'region';
}

export const AppCard = React.forwardRef<HTMLDivElement, AppCardProps>(
  (
    {
      className,
      noPadding = false,
      noBorder = false,
      interactive = false,
      padding = 'md',
      variant = 'card',
      asRole,
      ...props
    },
    ref,
  ) => {
    const surface =
      variant === 'muted'
        ? premiumTheme.surface.muted
        : variant === 'sunken'
        ? premiumTheme.surface.sunken
        : premiumTheme.surface.card;

    return (
      <div
        ref={ref}
        role={asRole}
        className={cn(
          surface,
          noBorder && 'border-0',
          !noPadding && premiumTheme.padding[padding],
          interactive && [
            premiumTheme.motion.hover,
            'hover:border-border-premium',
            'cursor-pointer',
          ],
          className,
        )}
        {...props}
      />
    );
  },
);
AppCard.displayName = 'AppCard';
